// src/components/Section/Section.tsx
import React, { useState, useRef } from 'react';
import { downloadCombinedCards } from '../../utils/downloadUtils';
import './Section.css';

interface SectionProps {
  type: 'hadith' | 'special';
  title?: string;
  subtitle?: string;
  arabicText: string;
  translation: string;
  paragraphs?: string[];
  context?: string;
  source?: string;
  borderColor?: string;
  showCombinedDownload?: boolean;
  introRef?: React.RefObject<HTMLDivElement | null>;
  showPreviousSectionDownload?: boolean;
  previousSectionRef?: React.RefObject<HTMLDivElement | null>;
  showIndividualDownload?: boolean;
}

const Section: React.FC<SectionProps> = ({
  type,
  title,
  subtitle,
  arabicText,
  translation,
  paragraphs,
  context,
  source,
  borderColor,
  showCombinedDownload,
  introRef,
  showPreviousSectionDownload,
  previousSectionRef,
  showIndividualDownload
}) => {
  const isSpecial = type === 'special';
  const cardBorderColor = borderColor || (isSpecial ? '#7c3aed' : '#f59e0b');
  const [isDownloadingCombined, setIsDownloadingCombined] = useState(false);
  const [isDownloadingWithPrevious, setIsDownloadingWithPrevious] = useState(false);
  const [isDownloadingIndividual, setIsDownloadingIndividual] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const handleDownloadCombined = async () => {
    if (!introRef?.current || !sectionRef.current || isDownloadingCombined) return;

    setIsDownloadingCombined(true);
    try {
      await downloadCombinedCards(
        [introRef.current, sectionRef.current],
        {
          fileName: 'introduction-et-premier-hadith',
          watermark: {
            logoPath: '/images/logoP&F.jpg',
            text: 'Traduit par Mâhi Bâyân'
          }
        }
      );
    } catch (error) {
      console.error('Erreur lors du téléchargement combiné:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloadingCombined(false);
    }
  };

  const handleDownloadWithPrevious = async () => {
    if (!previousSectionRef?.current || !sectionRef.current || isDownloadingWithPrevious) return;

    setIsDownloadingWithPrevious(true);
    try {
      await downloadCombinedCards(
        [previousSectionRef.current, sectionRef.current],
        {
          fileName: 'patience-priere-et-hadith-bukhari',
          watermark: {
            logoPath: '/images/logoP&F.jpg',
            text: 'Traduit par Mâhi Bâyân'
          }
        }
      );
    } catch (error) {
      console.error('Erreur lors du téléchargement combiné:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloadingWithPrevious(false);
    }
  };

  const handleDownloadIndividual = async () => {
    if (!sectionRef.current || isDownloadingIndividual) return;

    setIsDownloadingIndividual(true);
    try {
      await downloadCombinedCards(
        [sectionRef.current],
        {
          fileName: 'conclusion-cheykh-rouheyli',
          watermark: {
            logoPath: '/images/logoP&F.jpg',
            text: 'Traduit par Mâhi Bâyân'
          }
        }
      );
    } catch (error) {
      console.error('Erreur lors du téléchargement individuel:', error);
      alert('Une erreur est survenue lors du téléchargement.');
    } finally {
      setIsDownloadingIndividual(false);
    }
  };

  // Fonction pour formater le texte avec les débuts de hadiths en gras et les paragraphes
  const formatText = (text: string) => {
    // Patterns pour identifier les débuts de hadiths et citations
    const patterns = [
      /(Le Prophète ﷺ a dit :)/g,
      /(Allah dit :)/g,
      /(Le Prince des croyants.*? a dit :)/g,
      /(Le Prophète ﷺ a dit en voulant annoncer une bonne nouvelle :)/g,
      /(Ce hadith a été expliqué par un autre hadith où le Prophète ﷺ dit :)/g,
      /(Le Prophète ﷺ, quand.*? il disait :)/g,
      /(« Donne-nous du réconfort par la prière, ô Bilal ! »)/g
    ];

    let formattedText = text;
    
    // Appliquer les patterns pour mettre en gras
    patterns.forEach(pattern => {
      formattedText = formattedText.replace(pattern, '<strong>$1</strong>');
    });

    // Convertir les \n\n en paragraphes HTML
    formattedText = formattedText.replace(/\n\n/g, '</p><p>');
    formattedText = '<p>' + formattedText + '</p>';

    return formattedText;
  };

  // Fonction pour séparer le contenu avec bloc vert
  const parseContentWithGreenBlock = (text: string) => {
    const parts = text.split('**ALLAH_VERSE**');
    if (parts.length === 1) {
      return { beforeGreen: text, greenBlock: null, afterGreen: null };
    }
    
    const beforeGreen = parts[0];
    const remaining = parts[1].split('**END_ALLAH_VERSE**');
    const greenBlock = remaining[0];
    const afterGreen = remaining[1] || '';
    
    return { beforeGreen, greenBlock, afterGreen };
  };

  // Fonction pour séparer le contenu avec bloc de hadith jaune
  const parseContentWithHadithBlock = (text: string) => {
    const parts = text.split('**HADITH_CONTEXT**');
    if (parts.length === 1) {
      return { beforeHadith: text, hadithBlock: null, afterHadith: null };
    }
    
    const beforeHadith = parts[0];
    const remaining = parts[1].split('**END_HADITH_CONTEXT**');
    const hadithBlock = remaining[0];
    const afterHadith = remaining[1] || '';
    
    return { beforeHadith, hadithBlock, afterHadith };
  };

  return (
    <div
      ref={sectionRef}
      className={`invocation ${isSpecial ? 'special-section' : ''}`}
      style={{ borderLeftColor: cardBorderColor }}
    >
      {title && (
        <h4 className="section-title">{title}</h4>
      )}

      {subtitle && (
        <h4 className="section-title">{subtitle}</h4>
      )}

      {context && (
        <div className="hadith-context">
          <strong>{context}</strong>
        </div>
      )}

      {translation && (() => {
        const { beforeGreen, greenBlock, afterGreen } = parseContentWithGreenBlock(translation);
        const { beforeHadith, hadithBlock, afterHadith } = parseContentWithHadithBlock(greenBlock || translation);
        
        if (greenBlock) {
          // Si il y a un bloc avec verset d'Allah, on affiche la structure spéciale
          return (
            <>
              {beforeGreen && (
                <div 
                  className="translation" 
                  dangerouslySetInnerHTML={{ __html: formatText(beforeGreen) }}
                />
              )}
              
              <div className="allah-verse-block">
                <div className="allah-verse-intro">
                  <strong>Allah dit :</strong>
                </div>
                {arabicText && (
                  <div className="allah-verse-arabic">{arabicText}</div>
                )}
                <div 
                  className="allah-verse-translation"
                  dangerouslySetInnerHTML={{ __html: formatText(greenBlock) }}
                />
              </div>
              
              {afterGreen && (
                <div 
                  className="translation" 
                  dangerouslySetInnerHTML={{ __html: formatText(afterGreen) }}
                />
              )}
            </>
          );
        } else if (hadithBlock) {
          // Si il y a un bloc de hadith jaune (pour la conclusion)
          return (
            <>
              {beforeHadith && (
                <div 
                  className="translation" 
                  dangerouslySetInnerHTML={{ __html: formatText(beforeHadith) }}
                />
              )}
              
              <div 
                className="hadith-context"
                dangerouslySetInnerHTML={{ __html: formatText(hadithBlock) }}
              />
              
              {afterHadith && (
                <div 
                  className="translation" 
                  dangerouslySetInnerHTML={{ __html: formatText(afterHadith) }}
                />
              )}
            </>
          );
        } else {
          // Affichage normal
          return (
            <>
              {arabicText && (
                <div className="arabic-text">{arabicText}</div>
              )}
              <div 
                className="translation" 
                dangerouslySetInnerHTML={{ __html: formatText(translation) }}
              />
            </>
          );
        }
      })()}

      {paragraphs && paragraphs.map((paragraph, index) => (
        <div 
          key={index} 
          className="translation paragraph" 
          dangerouslySetInnerHTML={{ __html: formatText(paragraph) }}
        />
      ))}

      {source && (
        <div className="source-container">
          <div className="source">{source}</div>
          {showCombinedDownload && (
            <button
              className="download-button"
              onClick={handleDownloadCombined}
              disabled={isDownloadingCombined}
              aria-label="Télécharger cette section avec l'introduction"
            >
              {isDownloadingCombined ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Téléchargement...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <polyline 
                      points="7 10 12 15 17 10" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <line 
                      x1="12" 
                      y1="15" 
                      x2="12" 
                      y2="3" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  Télécharger
                </>
              )}
            </button>
          )}
          {showPreviousSectionDownload && (
            <button
              className="download-button"
              onClick={handleDownloadWithPrevious}
              disabled={isDownloadingWithPrevious}
              aria-label="Télécharger cette section avec la section précédente"
            >
              {isDownloadingWithPrevious ? (
                <>
                  <span className="loading-spinner">⏳</span>
                  Téléchargement...
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path 
                      d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <polyline 
                      points="7 10 12 15 17 10" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                    <line 
                      x1="12" 
                      y1="15" 
                      x2="12" 
                      y2="3" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                  Télécharger
                </>
              )}
            </button>
          )}
        </div>
      )}

      {showIndividualDownload && (
        <div className="individual-download-container">
          <button
            className="download-button"
            onClick={handleDownloadIndividual}
            disabled={isDownloadingIndividual}
            aria-label="Télécharger cette section"
          >
            {isDownloadingIndividual ? (
              <>
                <span className="loading-spinner">⏳</span>
                Téléchargement...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path 
                    d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <polyline 
                    points="7 10 12 15 17 10" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                  <line 
                    x1="12" 
                    y1="15" 
                    x2="12" 
                    y2="3" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                Télécharger cette carte
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Section;
